/**
 * InfoCityJobs — Map Provider Abstraction
 *
 * This file centralizes all map/geocoding configuration.
 * To swap providers: update only this file + corresponding .env variables.
 * MapView, SearchBar and all components import from here — never hardcode tile URLs.
 *
 * Current provider: OpenStreetMap (OSM) via Leaflet — free, no API key required.
 * Production options: Mapbox, Jawg, Stadia, self-hosted (switch via env vars).
 *
 * NOTE: When using OSM tiles in production, respect their usage policy:
 *   https://operations.osmfoundation.org/policies/tiles/
 * For high traffic, use a tile CDN or self-hosted tile server.
 *
 * Geocoding runs through the backend proxy /api/geocode to:
 *   - centralize rate limiting
 *   - keep API keys server-side
 *   - allow provider swap without frontend changes
 */

export const MAP_CONFIG = {
  /**
   * Tile provider — swap URL without touching MapView
   * Env: REACT_APP_MAP_TILE_URL
   */
  tiles: {
    url: process.env.REACT_APP_MAP_TILE_URL
      || "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: process.env.REACT_APP_MAP_ATTRIBUTION
      || '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 19,
    subdomains: "abc",
  },

  /**
   * Geocoding — always goes through backend proxy
   * Backend handles provider selection, API keys and rate limiting
   * Env: REACT_APP_GEOCODING_ENDPOINT (defaults to /api/geocode)
   */
  geocoding: {
    endpoint: process.env.REACT_APP_GEOCODING_ENDPOINT || "/api/geocode",
    reverseEndpoint: process.env.REACT_APP_REVERSE_GEOCODING_ENDPOINT || "/api/reverse-geocode",
  },

  /**
   * Map defaults — initial view for Spain (no country selected)
   */
  defaults: {
    center: [40.4168, -3.7038],   // Madrid
    zoom: 6,
    minZoom: 3,
    maxZoom: 18,
    country: "España",
    countryCode: "ES",
  },

  /**
   * Per-country fallback view (approximate geographic center) — used ONLY when
   * a country is selected but there are 0 mappable jobs for it (so the map
   * never silently falls back to the Spain default center for another
   * country). When jobs exist, FitBoundsToJobs takes over and this is ignored.
   */
  countryDefaults: {
    ES: { center: [40.4168, -3.7038], zoom: 6 },
    UY: { center: [-32.5228, -55.7658], zoom: 7 },
    US: { center: [39.8283, -98.5795], zoom: 4 },
    AR: { center: [-38.4161, -63.6167], zoom: 4 },
    BR: { center: [-14.2350, -51.9253], zoom: 4 },
  },

  /**
   * Cluster settings
   */
  clustering: {
    enabled: true,
    maxClusterRadius: 60,       // px — larger = more aggressive clustering
    disableClusteringAtZoom: 14, // individual pins at this zoom level
  },

  /**
   * Distance filter options (km) for "buscar cerca de mí"
   */
  distanceOptions: [1, 3, 5, 10, 25, 50],
  defaultDistance: 10,
};

export default MAP_CONFIG;
